/**
 * CEO Dashboard — extracted panels.
 * Boardroom tone: ceremonial typography, gold accents on signed
 * artefacts, restrained data density (no operational noise).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Award, ChevronRight, FileSignature } from "lucide-react";
import { useDashboardData } from "@/contexts/DashboardDataContext";
import { cn } from "@/lib/utils";
import {
  fmtAud,
  PanelCard,
  LinearProgress,
} from "./DashboardPrimitives";

// Stable colour palette for the vendor concentration bar.
const VENDOR_COLORS = ["#137DC5", "#0E8762", "#A87C2A", "#5B3FB0", "#B5651D"];

const roiTone = (roi) => (roi >= 3 ? "low" : roi >= 2 ? "tralenex" : "risk");
const coverageTone = (c) => (c >= 90 ? "low" : c >= 75 ? "tralenex" : "risk");

// ───────────────────────────────────────────────────────────────────────────
// Spend vs Value · 12-month SVG line chart
// ───────────────────────────────────────────────────────────────────────────
const CHART = {
  width: 720,
  height: 220,
  padX: 56, // wider left gutter to fit Y-axis labels (A$xM)
  padY: 16,
  gridStops: [0, 0.25, 0.5, 0.75, 1],
  valueStroke: "#0E8762",
  valueFill: "#0E8762",
  spendStroke: "#137DC5",
  gridStroke: "#E2E8F0",
  labelFill: "#475569",
};

// Format a raw AUD value (e.g. 12_500_000) into a short axis label
// (e.g. "A$12.5M"). Keeps both M and k formats readable.
function fmtAxisAud(v) {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `A$${m < 10 ? m.toFixed(1) : Math.round(m)}M`;
  }
  if (v >= 1_000) return `A$${Math.round(v / 1_000)}k`;
  return `A$${Math.round(v)}`;
}

function buildChartGeometry(data) {
  const { width, height, padX, padY } = CHART;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const maxV = Math.max(...data.map((d) => Math.max(d.spendAud, d.valueAud)));
  // Round the Y-axis ceiling up to a "nice" number so the gridline labels
  // read cleanly (e.g. A$0 / A$5M / A$10M / A$15M / A$20M instead of
  // A$0 / A$4.7M / A$9.4M / …).
  const niceMax = niceCeil(maxV);
  const x = (i) => padX + (i / (data.length - 1)) * innerW;
  const y = (v) => padY + innerH - (v / niceMax) * innerH;
  const valuePts = data.map((d, i) => `${x(i)},${y(d.valueAud)}`).join(" ");
  const spendPts = data.map((d, i) => `${x(i)},${y(d.spendAud)}`).join(" ");
  const valueArea =
    `M ${x(0)} ${padY + innerH} ` +
    data.map((d, i) => `L ${x(i)} ${y(d.valueAud)}`).join(" ") +
    ` L ${x(data.length - 1)} ${padY + innerH} Z`;
  return { innerW, innerH, x, y, valuePts, spendPts, valueArea, niceMax };
}

// Round a positive number up to a "nice" axis ceiling — 1, 2, 2.5, or 5
// times a power of ten. This gives cleanly-divisible gridlines.
function niceCeil(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pow;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return niceNorm * pow;
}

function ChartGrid({ innerW, innerH }) {
  return CHART.gridStops.map((p) => (
    <line
      key={p}
      x1={CHART.padX}
      y1={CHART.padY + innerH * (1 - p)}
      x2={CHART.padX + innerW}
      y2={CHART.padY + innerH * (1 - p)}
      stroke={CHART.gridStroke}
      strokeDasharray="2 4"
      strokeWidth="1"
    />
  ));
}

function ChartYLabels({ innerH, niceMax }) {
  return CHART.gridStops.map((p) => (
    <text
      key={p}
      x={CHART.padX - 6}
      y={CHART.padY + innerH * (1 - p) + 3}
      textAnchor="end"
      fontSize="10"
      fill={CHART.labelFill}
      fontFamily="JetBrains Mono, monospace"
    >
      {fmtAxisAud(niceMax * p)}
    </text>
  ));
}

function ChartXLabels({ data, x }) {
  return data.map((d, i) => (
    <text
      key={d.month}
      x={x(i)}
      y={CHART.height + 14}
      textAnchor="middle"
      fontSize="10"
      fill={CHART.labelFill}
      fontFamily="JetBrains Mono, monospace"
    >
      {d.month}
    </text>
  ));
}

function ChartLegend() {
  return (
    <div className="mt-2 flex items-center gap-4 text-meta">
      <span className="inline-flex items-center gap-1.5 text-ink-secondary">
        <span className="inline-block h-0.5 w-4 bg-risk-low" /> AI Value
      </span>
      <span className="inline-flex items-center gap-1.5 text-ink-secondary">
        <span
          className="inline-block h-0.5 w-4 bg-tralenex-600"
          style={{ borderTop: "2px dashed" }}
        />{" "}
        AI Spend
      </span>
    </div>
  );
}

export function SpendValueChart() {
  const { trend12m } = useDashboardData();
  if (!trend12m?.length) return null;

  const geo = buildChartGeometry(trend12m);

  return (
    <PanelCard
      title="AI Spend vs AI Value · last 12 months"
      testId="ceo-svv-panel"
      footer="Source: ALGOS — measured monthly · AUD"
    >
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${CHART.width} ${CHART.height + 24}`}
          className="w-full h-auto"
          role="img"
          aria-label="Spend vs Value"
        >
          <ChartGrid innerW={geo.innerW} innerH={geo.innerH} />
          <ChartYLabels innerH={geo.innerH} niceMax={geo.niceMax} />
          <path d={geo.valueArea} fill={CHART.valueFill} fillOpacity="0.08" />
          <polyline points={geo.valuePts} fill="none" stroke={CHART.valueStroke} strokeWidth="2" />
          <polyline
            points={geo.spendPts}
            fill="none"
            stroke={CHART.spendStroke}
            strokeWidth="2"
            strokeDasharray="4 3"
          />
          <ChartXLabels data={trend12m} x={geo.x} />
        </svg>
        <ChartLegend />
      </div>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Department ROI bars
// ───────────────────────────────────────────────────────────────────────────
export function CeoDepartmentsPanel() {
  const navigate = useNavigate();
  const { departmentRollups } = useDashboardData();

  return (
    <PanelCard
      title="Return on AI investment — by department"
      testId="ceo-departments-panel"
      action={
        <button
          type="button"
          onClick={() => navigate("/commercial")}
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 inline-flex items-center gap-1"
        >
          Commercial summary <ChevronRight size={12} />
        </button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {departmentRollups.slice(0, 8).map((d) => (
          <div key={d.department} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-ink-primary truncate font-medium">{d.department}</p>
                <span className="text-sm font-semibold text-ink-primary tabular-nums shrink-0">
                  {d.roi.toFixed(1)}×
                </span>
              </div>
              <LinearProgress value={Math.min(100, (d.roi / 5) * 100)} tone={roiTone(d.roi)} />
              <p className="mt-1 text-meta text-ink-secondary">
                {fmtAud(d.valueAud)} value · {fmtAud(d.spendAud)} spend
              </p>
            </div>
          </div>
        ))}
      </div>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Vendor concentration bar + legend
// ───────────────────────────────────────────────────────────────────────────
export function VendorConcentrationPanel() {
  const { vendorRollups } = useDashboardData();
  const vendors = vendorRollups.slice(0, 5);
  const totalSpend = vendorRollups.reduce((s, v) => s + v.spendAud, 0) || 1;
  const top3Share = Math.round(
    (vendorRollups.slice(0, 3).reduce((s, v) => s + v.spendAud, 0) / totalSpend) * 100,
  );

  return (
    <PanelCard
      title="Vendor concentration"
      testId="ceo-vendors-panel"
      footer={`Top 3 vendors = ${top3Share}% of spend · diversification ${top3Share <= 70 ? "healthy" : "watch"}`}
    >
      <div className="space-y-3">
        <div className="flex h-3 w-full rounded overflow-hidden border border-border-subtle">
          {vendors.map((v, i) => {
            const pct = (v.spendAud / totalSpend) * 100;
            return (
              <div
                key={v.id}
                style={{ width: `${pct}%`, background: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                title={`${v.name} · ${pct.toFixed(1)}%`}
                data-testid={`ceo-vendor-bar-${v.id}`}
              />
            );
          })}
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          {vendors.map((v, i) => {
            const pct = ((v.spendAud / totalSpend) * 100).toFixed(1);
            return (
              <li key={v.id} className="flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-sm shrink-0"
                  style={{ background: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                />
                <span className="flex-1 truncate text-ink-primary">{v.name}</span>
                <span className="text-meta text-ink-secondary tabular-nums">
                  {pct}% · {fmtAud(v.spendAud)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Governance posture
// ───────────────────────────────────────────────────────────────────────────
export function CeoGovernancePanel() {
  const { frameworks } = useDashboardData();
  return (
    <PanelCard title="Governance posture" testId="ceo-governance-panel">
      <ul className="space-y-2.5 -my-1">
        {frameworks.slice(0, 5).map((f) => (
          <li key={f.id}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-ink-primary truncate">{f.name}</p>
              <span className="text-meta text-ink-secondary tabular-nums">{f.coverage}%</span>
            </div>
            <LinearProgress value={f.coverage} tone={coverageTone(f.coverage)} />
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Signed board artefacts
// ───────────────────────────────────────────────────────────────────────────
export function SignedReportsPanel() {
  const { reports } = useDashboardData();
  return (
    <PanelCard
      title="Signed board artefacts"
      testId="ceo-reports-panel"
      action={
        <span className="inline-flex items-center gap-1 text-meta gold-seal">
          <Award size={12} /> Signed
        </span>
      }
    >
      <ul className="space-y-3 -my-1">
        {reports.slice(0, 4).map((r) => (
          <li key={r.id} className="flex items-start gap-3">
            <FileSignature size={14} className="text-gold-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-primary font-medium truncate">{r.name}</p>
              <p className="text-meta text-ink-secondary truncate">
                {r.generatedAt} · {r.framework}
              </p>
            </div>
            <span className="text-meta text-ink-disabled tabular-nums shrink-0">{r.sizeMb} MB</span>
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Estate at a glance
// ───────────────────────────────────────────────────────────────────────────
function EstateRow({ label, value, tone }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-ink-secondary">{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          tone === "risk" && value > 0 ? "text-risk-high" : "text-ink-primary",
        )}
      >
        {value}
      </span>
    </li>
  );
}

export function EstatePanel() {
  const { kpis, vendorRollups } = useDashboardData();
  return (
    <PanelCard title="Estate at a glance" testId="ceo-estate-panel">
      <ul className="space-y-2 -my-1 text-sm">
        <EstateRow label="Governed agents" value={kpis.governedAgents} />
        <EstateRow label="Ungoverned (shadow)" value={kpis.ungoverned} tone="risk" />
        <EstateRow label="Open high incidents" value={kpis.openHighIncidents} />
        <EstateRow label="Policy violations · 30d" value={kpis.violations} />
        <EstateRow label="Vendors connected" value={vendorRollups.length} />
      </ul>
    </PanelCard>
  );
}
